import { motion } from "framer-motion";
import { Ban, UserRound, Wrench, Headphones, Eye } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/site/Motion";

const Card = ({ className = "", bg = "", children, testid, delay = 0 }) => (
  <Reveal delay={delay} className={className}>
    <motion.div whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 260, damping: 22 }} data-testid={testid}
      className={`group relative h-full overflow-hidden rounded-3xl border border-emerald-900/10 p-7 sm:p-8 ${bg}`}>
      {children}
    </motion.div>
  </Reveal>
);

const Icon = ({ I, dark }) => (
  <span className={`grid h-12 w-12 place-items-center rounded-2xl transition-transform duration-500 group-hover:rotate-12 ${dark ? "bg-lime-400 text-[#050B1F]" : "bg-[#0B2A6F] text-lime-300"}`}><I className="h-6 w-6" /></span>
);

export const Bento = () => (
  <section id="benefits" className="bg-white px-4 py-24 sm:px-6 lg:px-12 lg:py-32" data-testid="benefits-section">
    <div className="mx-auto max-w-7xl">
      <Reveal className="max-w-3xl">
        <Eyebrow>The Zero difference</Eyebrow>
        <h2 className="mt-4 font-display text-4xl font-extrabold leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl">More money in your pocket. <span className="text-emerald-600">More people in your corner.</span></h2>
      </Reveal>
      <div className="mt-14 grid auto-rows-[minmax(240px,auto)] gap-5 md:grid-cols-6 lg:grid-cols-12">
        <Card className="md:col-span-6 lg:col-span-8" testid="bento-fees">
          <div className="absolute inset-0 bg-[#0B2A6F]" />
          <div className="absolute -right-20 -top-20 h-80 w-80 rounded-full bg-lime-400/30 blur-3xl transition-transform duration-700 group-hover:scale-125" />
          <div className="relative flex h-full flex-col justify-between gap-10 text-white">
            <Icon I={Ban} dark />
            <div>
              <p className="font-display text-5xl font-extrabold leading-[0.95] tracking-tight sm:text-6xl">Eliminate transaction fees.</p>
              <p className="mt-4 max-w-md text-emerald-100/85">Our fee elimination programs let you stop losing a slice of every sale. Keep what you earn and put it back into your business.</p>
            </div>
          </div>
        </Card>
        <Card className="md:col-span-6 lg:col-span-4 lg:row-span-2" testid="bento-equipment" delay={0.08}>
          <img src="/img/terminal-blue.jpg" alt="Card terminal" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050B1F] via-[#050B1F]/40 to-transparent" />
          <div className="relative flex h-full min-h-[420px] flex-col justify-end text-white">
            <span className="mb-4 w-fit rounded-full bg-lime-400 px-3 py-1 font-mono text-[11px] font-medium uppercase tracking-[0.15em] text-[#050B1F]">Qualifying merchants</span>
            <p className="font-display text-4xl font-extrabold leading-[0.95] tracking-tight">Free equipment.</p>
            <p className="mt-3 text-emerald-50/90">Modern terminals, mobile readers and POS systems at no cost to qualifying merchants.</p>
          </div>
        </Card>
        <Card className="md:col-span-3 lg:col-span-4" bg="bg-[#EAF3FF]" testid="bento-manager" delay={0.1}>
          <div className="flex h-full flex-col justify-between gap-8">
            <Icon I={UserRound} />
            <div><p className="font-display text-2xl font-extrabold tracking-tight">Dedicated account manager</p><p className="mt-2 text-sm text-emerald-950/75">One person who knows your business by name.</p></div>
          </div>
        </Card>
        <Card className="md:col-span-3 lg:col-span-4" bg="bg-lime-300" testid="bento-tech" delay={0.15}>
          <div className="flex h-full flex-col justify-between gap-8">
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-[#050B1F] text-lime-300 transition-transform duration-500 group-hover:rotate-12"><Wrench className="h-6 w-6" /></span>
            <div><p className="font-display text-2xl font-extrabold tracking-tight text-[#050B1F]">Tech support</p><p className="mt-2 text-sm text-[#0B2A6F]">Real humans to fix terminals, connections and setups fast.</p></div>
          </div>
        </Card>
        <Card className="md:col-span-3 lg:col-span-6" bg="bg-white" testid="bento-service" delay={0.1}>
          <div className="flex h-full flex-col justify-between gap-8">
            <Icon I={Headphones} />
            <div><p className="font-display text-2xl font-extrabold tracking-tight">Customer service</p><p className="mt-2 max-w-sm text-sm text-emerald-950/75">Friendly, responsive and on your side. No phone trees, no runaround.</p></div>
          </div>
        </Card>
        <Card className="md:col-span-3 lg:col-span-6" bg="bg-[#050B1F]" testid="bento-transparent" delay={0.15}>
          <div className="flex h-full flex-col justify-between gap-8 text-white">
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-white/10 text-lime-300 transition-transform duration-500 group-hover:rotate-12"><Eye className="h-6 w-6" /></span>
            <div><p className="font-display text-2xl font-extrabold tracking-tight">Transparent pricing</p><p className="mt-2 max-w-sm text-sm text-emerald-100/80">No hidden fees, no surprise line items. Everything explained up front.</p></div>
          </div>
        </Card>
      </div>
    </div>
  </section>
);
